import React, { useEffect, useRef, useState } from 'react';
import '../../styles/IndustriesOffer.css';
import '../../styles/GridCard.css';
import '../../styles/BenefitsCard.css';
import tickIcon from '../../assets/industries-Images/tickIcon.svg';

type Cards = {
  image: string;
  title: string;
  description: string;
  features?: string[];
};

type BenefitsCardProps = {
  sectionHeading: string;
  benefitsCards: Cards[];
};

// @ts-ignore
const imagesContext = require.context(
  '../../assets/serviceDetails/Benefits-card',
  false,
  /\.(png|jpe?g|svg)$/
);

const getImage = (imageName: string): string => {
  try {
    return imagesContext(`./${imageName}`);
  } catch {
    console.warn(`Image not found: ${imageName}`);
    return '';
  }
};

const BenefitsCard: React.FC<BenefitsCardProps> = ({
  sectionHeading,
  benefitsCards,
}) => {

  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateScrollProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    const updateScrollProgress = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const sectionTop = rect.top;
      const sectionHeight = rect.height;

      const startPoint = windowHeight;
      const endPoint = -sectionHeight;

      const totalDistance = startPoint - endPoint;
      const currentDistance = startPoint - sectionTop;
      const progress = Math.max(0, Math.min(1, currentDistance / totalDistance));

      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    updateScrollProgress();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const calculateVerticalOffset = () => {
    if (!sectionRef.current) return 0;

    const gridElement = sectionRef.current.querySelector('.benefitsCards-grid');
    if (!gridElement) return 0;

    const gridHeight = gridElement.getBoundingClientRect().height;
    const timelineStart = 0;
    const timelineEnd = gridHeight + 200;

    // Calculate offset based on scroll progress (from top to bottom)
    const offset = scrollProgress * (timelineEnd - timelineStart);

    return offset;
  };

  return (
    <section ref={sectionRef} className="gridCard-section benefitsCards-section">
      <div className="gridCard-content">
        <p className="sections-title gridCard-title">{sectionHeading}</p>
        <div className="benefitsCards-grid">
          <div
            className="benefitsCards-big-circle"
            style={{
              transform: `translate(-50%, calc(-50% + ${calculateVerticalOffset()}px))`
            }}
          />

          <div
            className="benefitsCards-dot"
            style={{
              transform: `translate(-50%, calc(-50% + ${calculateVerticalOffset()}px))`
            }}
          />

          {benefitsCards.map((card, index) => (
            <div
              key={index}
              className={`benefitsCards-row ${index % 2 === 0 ? 'even' : 'odd'
                }`}
            >
              {/* Image side */}
              <div className="benefitsCards-image-container">
                {/* Big background number */}
                <div className="benefitsCards-big-number">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className='benefitsCards-image-container-image'>
                  <img
                    src={getImage(card.image)}
                    alt={card.title}
                    className="benefitsCards-image"
                  />
                </div>
              </div>

              {/* Text content */}
              <div className="benefitsCards-card">
                <h3 className="benefitsCards-title">{card.title}</h3>
                <p className="benefitsCards-description">
                  {card.description}
                </p>
                <div className="benefitsCards-features">
                  {card.features?.slice(0, 3).map((feature, i) => (
                    <div key={i} className="benefitsCards-feature">
                      <div className="benefitsCards-feature-icon">
                        <img src={tickIcon} alt="tick" style={{ width: '20px', height: '20px' }} />
                      </div>
                      <div className="benefitsCards-feature-text">
                        {feature}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsCard;
